import {
  eachWeekOfInterval,
  endOfQuarter,
  format,
  getQuarter,
  getWeek,
  startOfQuarter,
} from "date-fns";

/**
 * The weeks a quarter's scorecard has columns for.
 *
 * Weeks start Sunday, matching both the plant's own scorecard and the roll
 * charts (see bucketShiftRolls, which buckets on date-fns' default Sunday
 * start). Postgres' DATE_TRUNC('week') starts Monday, so weekly bucketing is
 * deliberately done here rather than in SQL — a Monday boundary would split
 * the Sunday night shift off the week it belongs to.
 */
export type ScorecardWeek = {
  /** The Sunday the week starts, as a local Date. Keys the column. */
  start: Date;
  /** Week of year, counting Sunday-start weeks with week 1 holding Jan 1. */
  weekNum: number;
  /** "26-Jul" — the column heading, as the plant writes it. */
  label: string;
};

/**
 * Sunday-start weeks, numbered so week 1 is the one containing Jan 1. This is
 * the spreadsheet convention the plant already counts in (Excel's WEEKNUM), not
 * ISO — an ISO week starts Monday, which puts a Sunday in the week before and
 * numbers every column one lower.
 */
const WEEK_OPTIONS = { weekStartsOn: 0, firstWeekContainsDate: 1 } as const;

/** "Q3" */
export const quarterLabel = (within: Date) => `Q${getQuarter(within)}`;

/**
 * Every week column for the quarter `within` falls in.
 *
 * A week belongs to the quarter its *start* falls in, so the week straddling a
 * quarter boundary lands in one quarter or the other rather than being counted
 * in both. That leaves 13 columns for most quarters.
 */
export const quarterWeeks = (within: Date): ScorecardWeek[] => {
  const from = startOfQuarter(within);
  const to = endOfQuarter(within);

  return eachWeekOfInterval({ start: from, end: to }, WEEK_OPTIONS)
    // eachWeekOfInterval starts at the week *containing* `from`, which for a
    // quarter beginning mid-week starts in the quarter before.
    .filter((start) => start >= from)
    .map((start) => ({
      start,
      weekNum: getWeek(start, WEEK_OPTIONS),
      label: format(start, "d-MMM"),
    }));
};
